/**
 * Utilitários para retry de lazy loading com fallback para hard reload
 */

/**
 * Detecta erros de carregamento de chunk/módulo dinâmico em todos os navegadores.
 * Cobre as mensagens específicas do Chrome, Firefox e Safari (macOS/iOS).
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const message =
    (error as { message?: string })?.message ??
    (typeof error === 'string' ? error : '');
  if (!message) return false;
  return (
    // Chrome / Edge
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Failed to fetch') ||
    // Firefox
    message.includes('error loading dynamically imported module') ||
    // Safari (macOS / iOS)
    message.includes('Importing a module script failed') ||
    message.includes('dynamically imported module') ||
    message.includes('module script') ||
    message.includes('MIME Type')
  );
};

export const lazyRetry = (
  componentImport: () => Promise<any>,
  retriesLeft = 3,
  interval = 1000
): Promise<any> => {
  return new Promise((resolve, reject) => {
    componentImport()
      .then(resolve)
      .catch((error) => {
        // Se acabaram as tentativas, rejeitar
        if (retriesLeft === 0) {
          reject(error);
          return;
        }

        // Aguardar e tentar novamente
        setTimeout(() => {
          console.warn(`Falha ao carregar módulo, tentando novamente... (${retriesLeft} tentativas restantes)`);
          lazyRetry(componentImport, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};

/**
 * Versão com hard refresh em última instância
 */
export const lazyRetryWithRefresh = (
  componentImport: () => Promise<any>
): Promise<any> => {
  return new Promise((resolve, reject) => {
    componentImport()
      .then(resolve)
      .catch((error) => {
        console.error('Erro ao carregar módulo, forçando reload da página...');
        // Adicionar timestamp para forçar bypass de cache
        window.location.href = window.location.href.split('?')[0] + '?t=' + Date.now();
        reject(error);
      });
  });
};

/**
 * Retry robusto com auto-refresh para erros de chunk persistentes
 * Previne loops infinitos usando sessionStorage
 */
export const lazyRetryWithAutoRefresh = (
  componentImport: () => Promise<any>,
  retriesLeft = 3,
  interval = 1500
): Promise<any> => {
  return new Promise((resolve, reject) => {
    componentImport()
      .then((module) => {
        // Limpar flag de refresh ao carregar com sucesso
        sessionStorage.removeItem('chunk-refresh-attempted');
        resolve(module);
      })
      .catch((error) => {
        const isChunkError = isChunkLoadError(error);

        if (retriesLeft === 0) {
          // Se for erro de chunk e acabaram as tentativas, limpar caches e fazer auto-refresh
          if (isChunkError && !sessionStorage.getItem('chunk-refresh-attempted')) {
            console.warn('🔄 Chunk loading failed after retries. Clearing caches and refreshing...');
            sessionStorage.setItem('chunk-refresh-attempted', 'true');

            const hardReload = () => {
              const url = new URL(window.location.href);
              url.searchParams.set('cacheBust', Date.now().toString());
              window.location.replace(url.toString());
            };

            // Limpar caches do Service Worker antes de reload
            clearServiceWorkerCaches().then(hardReload).catch(hardReload);
            return;
          }
          // Limpar flag e rejeitar
          sessionStorage.removeItem('chunk-refresh-attempted');
          reject(error);
          return;
        }

        console.warn(`⚠️ Chunk load failed, retrying... (${retriesLeft} attempts left)`);
        setTimeout(() => {
          lazyRetryWithAutoRefresh(componentImport, retriesLeft - 1, interval)
            .then(resolve, reject);
        }, interval);
      });
  });
};

/**
 * Limpa todos os caches do Service Worker para evitar servir chunks antigos
 */
async function clearServiceWorkerCaches(): Promise<void> {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('🧹 Service Worker caches cleared');
    }
  } catch (e) {
    console.warn('Failed to clear caches:', e);
  }
}
